import React from "react";
import AffordabilityCalculator from "./AffordabilityCalculator";
import CostOfLivingForm from "./CostOfLivingForm";
import CostOfLivingSummary from "./CostOfLivingSummary";
import EstimatesDisclaimer from "./EstimatesDisclaimer";
import MyDayMap from "./MyDayMap";
import MyDayTimeline from "./MyDayTimeline";
import { useLivingToolsUiOptional } from "./LivingToolsContext";
import {
  CostOfLivingFormState,
  LivingToolsTab,
  MyDayPlan,
  PropertyLivingProperty,
} from "./types";

export type { PropertyLivingProperty };

export interface LivingToolsContentProps {
  property: PropertyLivingProperty;
  form: CostOfLivingFormState;
  onFormChange: (next: CostOfLivingFormState) => void;
  onPlanMyDay: () => void;
  isPlanning: boolean;
  planError: string;
  plan: MyDayPlan | null;
  showResults: boolean;
  variant: "inline" | "dock";
}

function showTab(
  variant: "inline" | "dock",
  tab: LivingToolsTab,
  activeTab: LivingToolsTab | null
): boolean {
  if (variant === "inline") return true;
  return activeTab === tab;
}

const LivingToolsContent: React.FC<LivingToolsContentProps> = ({
  property,
  form,
  onFormChange,
  onPlanMyDay,
  isPlanning,
  planError,
  plan,
  showResults,
  variant,
}) => {
  const ui = useLivingToolsUiOptional();
  const activeTab = ui?.activeTab ?? "plan";

  const affordability = (
    <AffordabilityCalculator
      price={property.price}
      rentFrequency={property.rent_frequency}
      deposit={property.deposit}
      postcode={property.postcode}
      bedrooms={property.bedrooms}
      internalArea={property.internal_area}
      description={property.description}
      householdIncome={form.householdIncome}
      incomeIsAnnual={form.incomeIsAnnual}
      occupants={form.occupants}
      includeBills={form.includeBills}
      includeTvLicence={form.includeTvLicence}
      includeContentsInsurance={form.includeContentsInsurance}
      onIncomeChange={(v) => onFormChange({ ...form, householdIncome: v })}
      onIncomePeriodChange={(annual) =>
        onFormChange({ ...form, incomeIsAnnual: annual })
      }
      onOccupantsChange={(v) => onFormChange({ ...form, occupants: v })}
      onIncludeBillsChange={(v) => onFormChange({ ...form, includeBills: v })}
      onIncludeTvLicenceChange={(v) =>
        onFormChange({ ...form, includeTvLicence: v })
      }
      onIncludeContentsInsuranceChange={(v) =>
        onFormChange({ ...form, includeContentsInsurance: v })
      }
    />
  );

  const planSection = (
    <>
      <CostOfLivingForm
        propertyLon={property.longitude}
        propertyLat={property.latitude}
        form={form}
        onChange={onFormChange}
        onPlanMyDay={onPlanMyDay}
        isPlanning={isPlanning}
      />
      {planError && (
        <div className="text-red-600 text-sm px-2">{planError}</div>
      )}
      {showResults && (
        <div className="bg-bg-white border border-solid border-[rgba(0,0,0,0.05)] shadow-soft overflow-hidden">
          <div className={variant === "dock" ? "flex flex-col" : "grid lg:grid-cols-3"}>
            <div className={variant === "dock" ? "" : "lg:col-span-2"}>
              <MyDayMap
                plan={plan}
                propertyLon={property.longitude}
                propertyLat={property.latitude}
              />
            </div>
            <div
              className={
                variant === "dock"
                  ? "border-t border-solid border-[rgba(0,0,0,0.06)]"
                  : "border-t lg:border-t-0 lg:border-l border-solid border-[rgba(0,0,0,0.06)]"
              }
            >
              <MyDayTimeline plan={plan} />
            </div>
          </div>
        </div>
      )}
    </>
  );

  const costSection = showResults ? (
    <CostOfLivingSummary
      price={property.price}
      rentFrequency={property.rent_frequency}
      deposit={property.deposit}
      postcode={property.postcode}
      bedrooms={property.bedrooms}
      internalArea={property.internal_area}
      description={property.description}
      form={form}
      plan={plan}
    />
  ) : (
    <p className="text-sm text-text-grey py-4">
      Plan your day first to see a full cost-of-living breakdown for this home.
    </p>
  );

  if (variant === "dock") {
    return (
      <div className="flex flex-col gap-4">
        {showTab(variant, "affordability", activeTab) && affordability}
        {showTab(variant, "plan", activeTab) && planSection}
        {showTab(variant, "cost", activeTab) && costSection}
        <EstimatesDisclaimer />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {affordability}
      {planSection}
      {costSection}
      <EstimatesDisclaimer />
    </div>
  );
};

export default LivingToolsContent;
